import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingBag, Heart, Star, ShieldCheck, Truck, Award, ArrowLeft } from "lucide-react";
import ProductCard from "../components/ProductCard";
import ColorSwatches from "../components/ColorSwatches";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [variant, setVariant] = useState(null);
  const { add, toggleWish, inWish } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data); setVariant(null);
      api.get(`/products?category=${encodeURIComponent(r.data.category)}`).then((rr) => setRelated(rr.data.filter((x) => x.id !== id).slice(0, 4)));
    });
  }, [id]);

  if (!p) return <div className="pt-40 container-x text-white/50">Loading…</div>;
  const variants = p.variants || [];
  const allSoldOut = variants.length > 0 && variants.every((v) => v.sold_out);
  const needsColor = variants.length > 0 && !variant;
  const blocked = needsColor || allSoldOut;
  const ctaLabel = allSoldOut ? "Sold out" : needsColor ? "Select a colour" : "Add to Cart";
  const mainImage = variant?.image || p.image;

  return (
    <div className="pt-32 pb-24 container-x" data-testid="product-detail-page">
      <Link to="/shop" className="text-sm text-white/50 hover:text-gold inline-flex items-center gap-2 mb-8"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>
      <div className="grid lg:grid-cols-2 gap-14">
        <div className="relative aspect-square rounded-3xl overflow-hidden bg-charcoal-800 grain">
          <img key={mainImage} src={mainImage} alt={variant ? `${p.name} - ${variant.name}` : p.name} className="w-full h-full object-cover animate-[fadeIn_.4s_ease]" data-testid="product-main-image" />
        </div>
        <div>
          <div className="kicker mb-3">{p.brand} · {p.category}</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight">{p.name}</h1>
          <div className="mt-4 flex items-center gap-3 text-sm">
            <div className="flex items-center gap-1"><Star className="w-4 h-4 fill-gold text-gold" /> {p.rating?.toFixed(1)}</div>
            <span className="text-white/30">·</span>
            <span className="text-white/50">{p.reviews || 24} reviews</span>
            <span className="text-white/30">·</span>
            <span className="text-gold">In Stock</span>
          </div>
          <p className="mt-6 text-white/70 leading-relaxed rich-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.description || "", { ALLOWED_TAGS: ["p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li", "a", "span", "div"], ALLOWED_ATTR: ["href", "target", "rel"] }) }}></p>
          <div className="mt-8 hairline"></div>
          <div className="mt-8">
            <div className="kicker mb-3">Specifications</div>
            <ul className="grid grid-cols-2 gap-2">
              {(p.specs || []).map((s, i) => <li key={i} className="glass rounded-lg px-3 py-2 text-sm text-white/80">{s}</li>)}
            </ul>
          </div>
          <div className="mt-8 flex items-baseline gap-4">
            <div className="text-4xl font-display">₹{p.price?.toLocaleString('en-IN')}</div>
            {p.original_price && <div className="text-lg text-white/40 line-through">₹{p.original_price?.toLocaleString('en-IN')}</div>}
          </div>
          {p.warranty?.trim() && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium" style={{ background: "rgba(138,106,15,0.08)", color: "#6B520B", boxShadow: "inset 0 0 0 1px rgba(138,106,15,0.25)" }} data-testid="product-warranty">
              <ShieldCheck className="w-4 h-4" /> {p.warranty.trim()}
            </div>
          )}
          {variants.length > 0 && (
            <div className="mt-8" data-testid="color-selector">
              <div className="kicker mb-3">Colour: <span className="normal-case tracking-normal" data-testid="selected-color-name">{allSoldOut ? "All colours sold out" : variant ? variant.name : "Please choose"}</span></div>
              <ColorSwatches variants={variants} selected={variant?.name} onSelect={setVariant} testPrefix="color-swatch" />
              {variants.some((v) => v.sold_out) && !allSoldOut && <div className="mt-2 text-xs text-white/50" data-testid="color-sold-out-note">Crossed-out colours are sold out.</div>}
            </div>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="glass rounded-full flex items-center">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-2 text-white/70 hover:text-gold" data-testid="qty-minus">−</button>
              <span className="px-3 min-w-[40px] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-2 text-white/70 hover:text-gold" data-testid="qty-plus">+</button>
            </div>
            <button onClick={() => add(p, qty, variant)} disabled={blocked} className={`btn-gold ${blocked ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="add-to-cart-btn"><ShoppingBag className="w-4 h-4" /> {ctaLabel}</button>
            <button onClick={() => toggleWish(p)} className="btn-ghost-outline" data-testid="wish-btn"><Heart className={`w-4 h-4 ${inWish(p.id) ? "fill-gold text-gold" : ""}`} /> Wishlist</button>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-3">
            {[{ i: Award, t: "Genuine" }, { i: Truck, t: "Free Delivery" }].map(({ i: I, t }) => (
              <div key={t} className="glass rounded-xl p-4 text-center">
                <I className="w-4 h-4 text-gold mx-auto mb-2" />
                <div className="text-xs text-white/70">{t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="font-display text-3xl mb-8">Related Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
